import { payment_method } from '@trashlab/api/src/domains/billing/schemas/payment-method';
import { customer_category } from '@trashlab/api/src/domains/core/schemas/base-template';
import {
  PgAddress,
  ValidatedAddress,
} from '@trashlab/api/src/domains/core/types';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import { customer_contact } from '@trashlab/api/src/domains/customer/schemas/customer-contact';
import { auto_pay_mode } from '@trashlab/api/src/domains/invoice/schemas/invoice-enums';
import { municipality } from '@trashlab/api/src/domains/pricing/schemas/municipality';
import {
  boolean,
  check,
  foreignKey,
  index,
  integer,
  pgEnum,
  pgTable,
  jsonb,
  text,
  uuid,
  varchar,
  timestamp,
  uniqueIndex,
} from 'drizzle-orm/pg-core';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { SQL, sql } from 'drizzle-orm';
import { identity_user } from '@trashlab/api/src/domains/identity/schemas/identity-user';

export const customer_site_status = pgEnum('customer_site_status', [
  'ACTIVE',
  'INACTIVE',
]);

export const customerSite = pgTable(
  'customer_site',
  {
    ...defaultFields.fieldsWithTenantId(),
    name: varchar('name', { length: 255 }).notNull(),
    jobSiteAccountId: varchar('jobSiteAccountId', { length: 50 }),
    address: jsonb('address').notNull().$type<PgAddress>(),
    validatedAddress: jsonb('validatedAddress').$type<ValidatedAddress>(),
    status: customer_site_status('status').notNull(),
    customerId: uuid('customerId').notNull(),
    addressString: text('addressString')
      .notNull()
      .generatedAlwaysAs(
        (): SQL => sql`((
          (((((((((${customerSite.address})::jsonb ->> 'streetAddressPartOne'::text) || ' '::text) ||
              ((address)::jsonb ->> 'streetAddressPartTwo'::text)) || ' '::text) ||
              ((address)::jsonb ->> 'city'::text)) || ' '::text) || ((address)::jsonb ->> 'state'::text)) || ' '::text) ||
              ((address)::jsonb ->> 'postalCode'::text)))`,
      ),
    notes: text('notes').default('').notNull(),
    latitude: varchar('latitude', { length: 20 }).notNull(),
    longitude: varchar('longitude', { length: 20 }).notNull(),
    primaryContactId: uuid('primaryContactId'),
    lastModifiedBy: uuid('lastModifiedBy'),
    receiveTextNotifications: boolean('receiveTextNotifications')
      .default(false)
      .notNull(),
    createdBy: uuid('createdBy'),
    abbreviation: varchar('abbreviation', { length: 8 }),
    purchaseOrderNumber: varchar('purchaseOrderNumber', { length: 255 }),
    searchables: text('searchables'),
    municipalityId: uuid('municipalityId'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    category: customer_category('category').default('RESIDENTIAL'),
    officeNotes: text('officeNotes').default('').notNull(),
    billingAddress: jsonb('billingAddress').$type<PgAddress>(),
    billingEmail: varchar('billingEmail'),
    billingName: varchar('billingName'),
    billingPhoneNumber: varchar('billingPhoneNumber'),
    defaultPaymentMethodId: uuid('defaultPaymentMethodId').references(
      () => payment_method.id,
    ),
    primaryBillingContactId: uuid('primaryBillingContactId').references(
      () => customer_contact.id,
    ),
    lastTouchedAtTs: timestamp('lastTouchedAtTs', { withTimezone: true }),
    salesRepId: uuid('salesRepId'),

    // LEED tracking
    /** Whether this customer site requires LEED tracking for dump tickets */
    isLeedRequired: boolean('isLeedRequired').notNull().default(false),

    // Per-site autopay configuration (all nullable to allow inheritance from customer)
    /**
     * Enable/disable autopay for this job site.
     * - true: Enable autopay for invoices related to this site
     * - false: Disable autopay for invoices related to this site
     * - null: Inherit from customer settings
     */
    sendBillingAutoPay: boolean('sendBillingAutoPay'),

    /**
     * Days after invoice date to schedule autopay.
     * - Positive values: X days after invoice date
     * - Negative values: X days before invoice date
     * - 0: Same day as invoice
     * - null: Inherit from customer settings
     */
    autoPayDays: integer('autoPayDays'),

    /**
     * Autopay scheduling mode for this site.
     * - DAYS_AFTER_INVOICE: Use autoPayDays to schedule X days after invoice date
     * - DAY_OF_MONTH: Use autoPayDayOfMonth to schedule on a specific day each month
     * - null: Inherit from customer settings
     */
    autoPayMode: auto_pay_mode('autoPayMode'),

    /**
     * Day of month (1-31) when autopay should be scheduled.
     * Only used when autoPayMode is DAY_OF_MONTH.
     * If the month has fewer days, autopay is scheduled on the last day.
     * - null: Inherit from customer settings
     */
    autoPayDayOfMonth: integer('autoPayDayOfMonth'),
    // Permanent merge alias. Archive state remains a visibility-only toggle.
    mergedIntoCustomerSiteId: uuid('mergedIntoCustomerSiteId'),
  },
  (table) => {
    return {
      address_trgm_gist_idx: index('customer_site_address_trgm_gist_idx').using(
        'gist',
        table.addressString.op('gist_trgm_ops'),
      ),
      address_street1_trgm_idx: index(
        'customer_site_address_street1_trgm_idx',
      ).using(
        'gin',
        sql`(COALESCE(${table.address} ->> 'streetAddressPartOne', '')) public.gin_trgm_ops`,
      ),
      address_street2_trgm_idx: index(
        'customer_site_address_street2_trgm_idx',
      ).using(
        'gin',
        sql`(COALESCE(${table.address} ->> 'streetAddressPartTwo', '')) public.gin_trgm_ops`,
      ),
      address_city_trgm_idx: index('customer_site_address_city_trgm_idx').using(
        'gin',
        sql`(COALESCE(${table.address} ->> 'city', '')) public.gin_trgm_ops`,
      ),
      address_state_trgm_idx: index(
        'customer_site_address_state_trgm_idx',
      ).using(
        'gin',
        sql`(COALESCE(${table.address} ->> 'state', '')) public.gin_trgm_ops`,
      ),
      address_postal_code_trgm_idx: index(
        'customer_site_address_postal_code_trgm_idx',
      ).using(
        'gin',
        sql`(COALESCE(${table.address} ->> 'postalCode', '')) public.gin_trgm_ops`,
      ),
      createdAtTs_idx: index('customer_site_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      name_trgm_gist_idx: index('customer_site_name_trgm_gist_idx').using(
        'gist',
        table.name.op('gist_trgm_ops'),
      ),
      tenantId_idx: index('customer_site_tenantId_idx').on(table.tenantId),
      rental_map_latitude_idx: index(
        'customer_site_rental_map_latitude_idx',
      ).on(
        table.tenantId,
        sql`public.try_parse_rental_map_coordinate(${table.latitude})`,
      ),
      rental_map_longitude_idx: index(
        'customer_site_rental_map_longitude_idx',
      ).on(
        table.tenantId,
        sql`public.try_parse_rental_map_coordinate(${table.longitude})`,
      ),
      trigram: index('customer_site_trigram').using(
        'gin',
        table.tenantId,
        table.searchables.op('gin_trgm_ops'),
      ),
      account_id_search_idx: index('customer_site_account_id_search_idx').using(
        'gin',
        table.tenantId,
        table.jobSiteAccountId.op('gin_trgm_ops'),
      ),
      updatedAtTs_idx: index('customer_site_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('customer_site_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      tenant_updatedAtTs_id_active_idx: index(
        'customer_site_tenant_updatedAtTs_id_active_idx',
      )
        .on(table.tenantId, table.updatedAtTs.desc(), table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_normalized_name_active_idx: index(
        'customer_site_tenant_normalized_name_active_idx',
      )
        .on(
          table.tenantId,
          sql`lower(btrim(${table.name}, U&'\\0009\\000A\\000B\\000C\\000D\\0020\\00A0\\1680\\2000\\2001\\2002\\2003\\2004\\2005\\2006\\2007\\2008\\2009\\200A\\2028\\2029\\202F\\205F\\3000\\FEFF'))`,
          table.customerId,
          table.id,
        )
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_customer_legacy_active_idx: index(
        'customer_site_tenant_customer_legacy_active_idx',
      )
        .on(table.tenantId, table.customerId, table.legacySystemId, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_name_active_idx: index('customer_site_tenant_name_active_idx')
        .on(table.tenantId, table.name, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_name_idx: index('customer_site_tenant_name_idx').on(
        table.tenantId,
        table.name,
        table.id,
      ),
      quote_global_name_sort_idx: index(
        'customer_site_quote_global_name_sort_idx',
      ).on(table.tenantId, sql`lower(${table.name}) COLLATE "C"`, table.id),
      versionNumber_idx: index('customer_site_versionNumber_idx').on(
        table.versionNumber,
      ),

      customerId_idx: index('customer_site_customerId_idx').on(
        table.customerId,
      ),
      customer_merge_page_idx: index(
        'customer_site_customer_merge_page_idx',
      ).on(table.tenantId, table.customerId, table.id),

      tenant_customer_idx: index('idx_customer_site_tenant_customer').on(
        table.tenantId,
        table.customerId,
      ),
      tenant_customer_active_idx: index(
        'customer_site_tenant_customer_active_idx',
      )
        .on(table.tenantId, table.customerId)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_customer_name_active_idx: index(
        'customer_site_tenant_customer_name_active_idx',
      )
        .on(table.tenantId, table.customerId, table.name, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_customer_name_idx: index(
        'customer_site_tenant_customer_name_idx',
      ).on(table.tenantId, table.customerId, table.name, table.id),
      tenant_customer_autopay_active_idx: index(
        'customer_site_tenant_customer_autopay_active_idx',
      )
        .on(table.tenantId, table.customerId)
        .where(
          sql`${table.archivedAtTs} IS NULL AND ${table.sendBillingAutoPay} = true`,
        ),
      jobSiteAccountId_tenantId_unique: uniqueIndex(
        'customer_site_jobSiteAccountId_tenantId_unique',
      ).on(table.tenantId, table.jobSiteAccountId),

      municipalityId_idx: index('customer_site_municipalityId_idx').on(
        table.municipalityId,
      ),
      primaryContactId_idx: index('customer_site_primaryContactId_idx').on(
        table.primaryContactId,
      ),
      primaryBillingContactId_idx: index(
        'customer_site_primaryBillingContactId',
      ).on(table.primaryBillingContactId),
      salesRepId_idx: index('customer_site_salesRepId_idx').on(
        table.salesRepId,
      ),
      defaultPaymentMethodId_idx: index(
        'customer_site_defaultPaymentMethodId_idx',
      ).on(table.defaultPaymentMethodId),
      mergedIntoCustomerSiteId_idx: index(
        'customer_site_mergedIntoCustomerSiteId_idx',
      )
        .on(table.tenantId, table.mergedIntoCustomerSiteId, table.id)
        .where(sql`${table.mergedIntoCustomerSiteId} IS NOT NULL`),
      mergedIntoCustomerSiteId_not_self_chk: check(
        'customer_site_mergedIntoCustomerSiteId_not_self_chk',
        sql`${table.mergedIntoCustomerSiteId} IS NULL OR ${table.mergedIntoCustomerSiteId} <> ${table.id}`,
      ),

      customer_site_customerId_fkey: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'customer_site_customerId_fkey',
      }),
      customer_site_municipalityId_fkey: foreignKey({
        columns: [table.municipalityId],
        foreignColumns: [municipality.id],
        name: 'customer_site_municipalityId_fkey',
      }),
      customer_site_primaryContactId_fkey: foreignKey({
        columns: [table.primaryContactId],
        foreignColumns: [customer_contact.id],
        name: 'customer_site_primaryContactId_fkey',
      }),
      customer_site_salesRepId_fkey: foreignKey({
        columns: [table.salesRepId],
        foreignColumns: [identity_user.id],
        name: 'customer_site_salesRepId_fkey',
      }).onDelete('set null'),
    };
  },
);

export const customer_site_history = pgTable('customer_site_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  id: uuid('id'),
  name: varchar('name', { length: 255 }),
  jobSiteAccountId: varchar('jobSiteAccountId', { length: 50 }),
  address: jsonb('address').$type<PgAddress>(),
  validatedAddress: jsonb('validatedAddress').$type<ValidatedAddress>(),
  status: customer_site_status('status'),
  customerId: uuid('customerId'),
  addressString: text('addressString'),
  notes: text('notes'),
  latitude: varchar('latitude', { length: 20 }),
  longitude: varchar('longitude', { length: 20 }),
  primaryContactId: uuid('primaryContactId'),
  lastModifiedBy: uuid('lastModifiedBy'),
  receiveTextNotifications: boolean('receiveTextNotifications'),
  createdBy: uuid('createdBy'),
  abbreviation: varchar('abbreviation', { length: 8 }),
  purchaseOrderNumber: varchar('purchaseOrderNumber', { length: 255 }),
  searchables: text('searchables'),
  municipalityId: uuid('municipalityId'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  category: customer_category('category'),
  officeNotes: text('officeNotes'),
  billingAddress: jsonb('billingAddress').$type<PgAddress>(),
  billingEmail: varchar('billingEmail'),
  billingName: varchar('billingName'),
  billingPhoneNumber: varchar('billingPhoneNumber'),
  defaultPaymentMethodId: uuid('defaultPaymentMethodId'),
  primaryBillingContactId: uuid('primaryBillingContactId'),
  lastTouchedAtTs: timestamp('lastTouchedAtTs', { withTimezone: true }),
  salesRepId: uuid('salesRepId'),
  isLeedRequired: boolean('isLeedRequired'),
  // Per-site autopay configuration
  sendBillingAutoPay: boolean('sendBillingAutoPay'),
  autoPayDays: integer('autoPayDays'),
  autoPayMode: auto_pay_mode('autoPayMode'),
  autoPayDayOfMonth: integer('autoPayDayOfMonth'),
  mergedIntoCustomerSiteId: uuid('mergedIntoCustomerSiteId'),
});

export const additional_customer_site_billing_recipient = pgTable(
  'additional_customer_site_billing_recipient',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    customerSiteId: uuid('customerSiteId').notNull(),
    contactId: uuid('contactId').notNull(),
  },
  (table) => {
    return {
      site_merge_page_idx: index('additional_recipient_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      createdAtTs_idx: index(
        'additional_customer_site_billing_recipient_createdAtTs_idx',
      ).on(table.createdAtTs),
      tenantId_idx: index(
        'additional_customer_site_billing_recipient_tenantId_idx',
      ).on(table.tenantId),
      updatedAtTs_idx: index(
        'additional_customer_site_billing_recipient_updatedAtTs_idx',
      ).on(table.updatedAtTs),
      updatedAtTs_idx1: index(
        'additional_customer_site_billing_recipient_updatedAtTs_idx1',
      ).on(table.updatedAtTs),
      versionNumber_idx: index(
        'additional_customer_site_billing_recipient_versionNumber_idx',
      ).on(table.versionNumber),
      contactId_idx: index(
        'additional_customer_site_billing_recipient_contactId_idx',
      ).on(table.contactId),
      customerSiteId_idx: index(
        'additional_customer_site_billing_recipient_customerSiteId_idx',
      ).on(table.customerSiteId),
    };
  },
);

export const additional_customer_site_billing_recipient_history = pgTable(
  'additional_customer_site_billing_recipient_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    customerSiteId: uuid('customerSiteId'),
    contactId: uuid('contactId'),
  },
);

export type CustomerSiteSchema = typeof customerSite.$inferSelect;
